/**
 * The permission catalogue — the single source of truth shared by the NestJS
 * guards and the React UI. Adding an entry to `CATALOG` (and re-running the
 * seed) is the only supported way to introduce a new capability.
 */
export const PERMISSION_GROUPS = {
    ORGANIZATION: 'Organization',
    USERS: 'Users & Access',
    BRANCHES: 'Branches & Warehouses',
    CATALOG: 'Catalog',
    INVENTORY: 'Inventory',
    SALES: 'Sales',
    POS: 'Point of Sale',
    CUSTOMERS: 'Customers',
    SUPPLIERS: 'Suppliers',
    PURCHASES: 'Purchases',
    FINANCE: 'Finance',
    MANUFACTURING: 'Manufacturing',
    RESTAURANT: 'Restaurant',
    DELIVERY: 'Delivery',
    REPORTS: 'Reports',
    ADMIN: 'Administration',
};
const G = PERMISSION_GROUPS;
/** `code -> [group, description]` */
const CATALOG = {
    ORGANIZATION_VIEW: [G.ORGANIZATION, 'View business profile and settings'],
    ORGANIZATION_UPDATE: [G.ORGANIZATION, 'Update business profile'],
    ORGANIZATION_SETTINGS_MANAGE: [G.ORGANIZATION, 'Change business settings, tax and numbering'],
    ORGANIZATION_DELETE: [G.ORGANIZATION, 'Delete the business'],
    USER_VIEW: [G.USERS, 'View members and employees'],
    USER_INVITE: [G.USERS, 'Invite new members'],
    USER_MANAGE: [G.USERS, 'Update, suspend or remove members'],
    ROLE_VIEW: [G.USERS, 'View roles and permissions'],
    ROLE_MANAGE: [G.USERS, 'Create and modify roles'],
    DEPARTMENT_MANAGE: [G.USERS, 'Manage departments'],
    BRANCH_VIEW: [G.BRANCHES, 'View branches'],
    BRANCH_CREATE: [G.BRANCHES, 'Create branches'],
    BRANCH_UPDATE: [G.BRANCHES, 'Update branches'],
    BRANCH_DELETE: [G.BRANCHES, 'Delete branches'],
    WAREHOUSE_VIEW: [G.BRANCHES, 'View warehouses'],
    WAREHOUSE_CREATE: [G.BRANCHES, 'Create warehouses'],
    WAREHOUSE_UPDATE: [G.BRANCHES, 'Update warehouses'],
    WAREHOUSE_DELETE: [G.BRANCHES, 'Delete warehouses'],
    PRODUCT_VIEW: [G.CATALOG, 'View products'],
    PRODUCT_CREATE: [G.CATALOG, 'Create products'],
    PRODUCT_UPDATE: [G.CATALOG, 'Update products'],
    PRODUCT_DELETE: [G.CATALOG, 'Delete products'],
    PRODUCT_COST_VIEW: [G.CATALOG, 'See product cost prices'],
    CATEGORY_MANAGE: [G.CATALOG, 'Manage categories and brands'],
    UNIT_MANAGE: [G.CATALOG, 'Manage units and conversions'],
    PRICING_VIEW: [G.CATALOG, 'View price lists'],
    PRICING_MANAGE: [G.CATALOG, 'Manage price lists and tiers'],
    PROMOTION_MANAGE: [G.CATALOG, 'Manage promotions'],
    STOCK_VIEW: [G.INVENTORY, 'View stock levels and the inventory ledger'],
    STOCK_ADJUST: [G.INVENTORY, 'Post stock adjustments'],
    STOCK_TRANSFER: [G.INVENTORY, 'Create stock transfers'],
    STOCK_TRANSFER_APPROVE: [G.INVENTORY, 'Approve, dispatch and receive transfers'],
    STOCK_COUNT: [G.INVENTORY, 'Run stock counts'],
    STOCK_COUNT_APPROVE: [G.INVENTORY, 'Approve stock counts and post variances'],
    BATCH_MANAGE: [G.INVENTORY, 'Manage batches and expiry'],
    SERIAL_MANAGE: [G.INVENTORY, 'Manage serial numbers and warranties'],
    SALE_VIEW: [G.SALES, 'View sales'],
    SALE_CREATE: [G.SALES, 'Create sales, quotations and orders'],
    SALE_UPDATE: [G.SALES, 'Update draft sales'],
    SALE_CANCEL: [G.SALES, 'Cancel sales'],
    SALE_DELETE: [G.SALES, 'Hide a sale from the sales list, and read hidden ones'],
    PROPERTY_VIEW: [G.SALES, 'View properties and land sales'],
    PROPERTY_MANAGE: [G.SALES, 'Add and edit properties'],
    PROPERTY_SELL: [G.SALES, 'Sell a property and take payment for it'],
    SALE_REFUND: [G.SALES, 'Refund sales and approve returns'],
    SALE_DISCOUNT: [G.SALES, 'Apply manual discounts'],
    INVOICE_VIEW: [G.SALES, 'View invoices'],
    INVOICE_ISSUE: [G.SALES, 'Issue invoices'],
    POS_ACCESS: [G.POS, 'Use the point of sale'],
    POS_SESSION_OPEN: [G.POS, 'Open and close register sessions'],
    POS_SESSION_RECONCILE: [G.POS, 'Reconcile register cash'],
    CUSTOMER_VIEW: [G.CUSTOMERS, 'View customers'],
    CUSTOMER_CREATE: [G.CUSTOMERS, 'Create customers'],
    CUSTOMER_UPDATE: [G.CUSTOMERS, 'Update customers'],
    CUSTOMER_DELETE: [G.CUSTOMERS, 'Delete customers'],
    CUSTOMER_CREDIT_MANAGE: [G.CUSTOMERS, 'Set credit limits and payment terms'],
    SUPPLIER_VIEW: [G.SUPPLIERS, 'View suppliers'],
    SUPPLIER_CREATE: [G.SUPPLIERS, 'Create suppliers'],
    SUPPLIER_UPDATE: [G.SUPPLIERS, 'Update suppliers'],
    SUPPLIER_DELETE: [G.SUPPLIERS, 'Delete suppliers'],
    PURCHASE_VIEW: [G.PURCHASES, 'View purchase requests and orders'],
    PURCHASE_CREATE: [G.PURCHASES, 'Create purchase requests and orders'],
    PURCHASE_UPDATE: [G.PURCHASES, 'Update draft purchase documents'],
    PURCHASE_APPROVE: [G.PURCHASES, 'Approve purchase requests and orders'],
    PURCHASE_RECEIVE: [G.PURCHASES, 'Receive goods'],
    PURCHASE_RETURN: [G.PURCHASES, 'Return goods to suppliers'],
    PAYMENT_VIEW: [G.FINANCE, 'View payments'],
    PAYMENT_CREATE: [G.FINANCE, 'Record payments'],
    PAYMENT_DELETE: [G.FINANCE, 'Void payments'],
    EXPENSE_VIEW: [G.FINANCE, 'View expenses'],
    EXPENSE_CREATE: [G.FINANCE, 'Record expenses'],
    EXPENSE_APPROVE: [G.FINANCE, 'Approve expenses'],
    ACCOUNT_MANAGE: [G.FINANCE, 'Manage cash and bank accounts'],
    RECEIVABLE_VIEW: [G.FINANCE, 'View receivables'],
    PAYABLE_VIEW: [G.FINANCE, 'View payables'],
    BOM_VIEW: [G.MANUFACTURING, 'View bills of materials'],
    BOM_MANAGE: [G.MANUFACTURING, 'Manage bills of materials'],
    PRODUCTION_VIEW: [G.MANUFACTURING, 'View production orders'],
    PRODUCTION_MANAGE: [G.MANUFACTURING, 'Create and complete production orders'],
    MENU_MANAGE: [G.RESTAURANT, 'Manage menu items and recipes'],
    TABLE_MANAGE: [G.RESTAURANT, 'Manage tables'],
    KITCHEN_ACCESS: [G.RESTAURANT, 'Access the kitchen display and orders'],
    DELIVERY_VIEW: [G.DELIVERY, 'View deliveries'],
    DELIVERY_MANAGE: [G.DELIVERY, 'Dispatch and update deliveries'],
    DRIVER_MANAGE: [G.DELIVERY, 'Manage drivers'],
    REPORT_VIEW: [G.REPORTS, 'View reports'],
    REPORT_FINANCIAL_VIEW: [G.REPORTS, 'View financial and profit reports'],
    REPORT_EXPORT: [G.REPORTS, 'Export report data'],
    AUDIT_VIEW: [G.ADMIN, 'View the audit log'],
    INTEGRATION_MANAGE: [G.ADMIN, 'Manage integrations and API keys'],
    IMPORT_MANAGE: [G.ADMIN, 'Import and export data'],
    SUBSCRIPTION_MANAGE: [G.ADMIN, 'Manage the subscription and billing'],
};
/** Typed constants: `P.SALE_REFUND` is `'SALE_REFUND'`, not `string`. */
export const PERMISSIONS = Object.fromEntries(Object.keys(CATALOG).map((code) => [code, code]));
export const P = PERMISSIONS;
export const PERMISSION_DEFINITIONS = Object.entries(CATALOG).map(([code, [group, description]]) => ({ code, group, description }));
export const ALL_PERMISSION_CODES = PERMISSION_DEFINITIONS.map((p) => p.code);
export function isPermissionCode(value) {
    return Object.prototype.hasOwnProperty.call(CATALOG, value);
}
//# sourceMappingURL=permissions.js.map